import PageWrapper from 'hoc/PageWrapper'
import {
  Badge,
  BadgeProps,
  Box,
  Button,
  FormControl,
  Grid,
  Paper,
  Stack,
  styled,
  Tab,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { TabContext, TabList, TabPanel } from '@mui/lab'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FileUpload } from 'components/FileUpload'
import { Add } from '@mui/icons-material'
import { DateTimePicker } from '@mui/x-date-pickers'
import { StepsSection } from './steps-section'
import { useMemo, useState } from 'react'
import { DialogAddCommitteeMembers } from './dialog-add-committee-members'
import { useParams } from 'react-router-dom'
import {
  useCreateTenderMutation,
  useNotificationsPollQuery,
  useTenderQuery,
} from 'services/tender'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { TenderStickyHeader } from './tender-sticky-header'
import { ETenderStatus, ITender } from 'types/tender'
import { TenderContactPeople } from 'pages/tender/tender-people'
import {
  formatDateTimePickerValue,
  formatTimeStampToDate,
} from 'hooks/date/formattedTime'
import { TenderCommitteeMembersTable } from './tender-committee-members-table'
import {
  useCreateTenderCommissionMutation,
  useTenderCommissionsQuery,
} from 'services/tender/tenderCommissions'
import { TenderInvitationSection } from './invitation-section/tender-invitation-section'
import { LotCard } from './lot-card'
import { QuestionAndAnswers } from './question-answers/question-answers'
import { useQueryState } from 'react-router-use-location-state'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { useTenderPassedDocDeadline } from 'hooks/date/useTenderPassedDocDeadline'
import { useUserCurrentQuery } from 'services/users'
import { IFile } from 'types/file'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -6,
    top: -5,
  },
}))

enum TenderTabValues {
  GENERAL = 'GENERAL',
  QUESTION_ANSWERS = 'QUESTION_ANSWERS',
}

type Props = {
  tender: ITender
}

function EditTender({ tender }: Props) {
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_page, setPage] = useQueryState('page', 0)
  const hasDocDeadlinePassed = useTenderPassedDocDeadline(tender)
  const [tab, setTab] = useState<TenderTabValues>(TenderTabValues.GENERAL)
  const [isAddMembersDialogOpen, setIsAddMembersDialogOpen] = useState(false)

  const tenderTabs = useMemo(
    () => [
      {
        value: TenderTabValues.GENERAL,
        label: t('tenderGeneralInfo'),
        disabled: false,
      },
      {
        value: TenderTabValues.QUESTION_ANSWERS,
        label: `${t('questionAndAnswers')}/${t('complaints')}`,
        disabled: false,
      },
    ],
    [t]
  )

  const { data: pollData } = useNotificationsPollQuery(
    {
      tenderId: tender.id,
    },
    {
      skip: !tender.id,
      pollingInterval: 60000,
    }
  )

  const { data: currentUser, isLoading } = useUserCurrentQuery()

  const { data: tenderCommission } = useTenderCommissionsQuery({
    tenderId: tender?.id ?? 0,
  })

  const [updateCommission] = useCreateTenderCommissionMutation()

  const updateTenderCommissionFileId = (fileId: string | null) => {
    if (tenderCommission) {
      updateCommission({
        ...tenderCommission,
        orderFileId: fileId,
      }).then((res) => {
        if ('error' in res) {
          toast.error(t('failedToCreate'))
        }
      })
    }
  }

  const handleCommissionFileUpload = (file: IFile) => {
    updateTenderCommissionFileId(file.id)
  }

  const handleCommissionFileDelete = () => {
    updateTenderCommissionFileId(null)
  }

  const [createTender] = useCreateTenderMutation()

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<ITender>({
    defaultValues: {
      name: tender ? tender.name : '',
      organizationName: tender ? tender.organizationName : '',
      method: tender?.method,
      startTime: tender?.startTime
        ? formatDateTimePickerValue(tender.startTime)
        : null,
      envelopeOpeningTime: tender?.envelopeOpeningTime
        ? formatDateTimePickerValue(tender.envelopeOpeningTime)
        : null,
      docSubmissionDeadline: tender?.docSubmissionDeadline
        ? formatDateTimePickerValue(tender.docSubmissionDeadline)
        : null,
    },
  })

  const onSubmit = async (values: ITender) => {
    const mappedData = {
      ...tender,
      ...values,
    }

    if (tender.status !== ETenderStatus.PUBLISHED) {
      if (dayjs(mappedData.startTime).isBefore(new Date())) {
        toast.warning(t('startDateShouldBeFuture'))
        return
      }

      if (
        dayjs(mappedData.docSubmissionDeadline).isBefore(dayjs(mappedData.startTime))
      ) {
        toast.warning(t('docSubmissionDeadlineShouldBeAfterStart'))
        return
      }

      //TODO enable once testing is done. comments to speed up tests only
      // if (dayjs(mappedData.envelopeOpeningTime).isBefore(dayjs(mappedData.docSubmissionDeadline).add(1, 'day'))) {
      //   toast.warning(t('envelopeOpeningTimeShouldBeFuture'))
      //   return
      // }
    }

    const createData = await createTender(mappedData)

    if ('error' in createData) {
      toast.error(t('failedToCreate'))
    }
  }

  const tenderStartTime = watch('startTime')
  const tenderDocSubmissionDeadline = watch('docSubmissionDeadline')
  const envelopeOpeningTime = watch('envelopeOpeningTime')
  const envelopeOpeningMinDate = dayjs(tenderDocSubmissionDeadline).add(1, 'day')

  const editingDisabled = tender?.docSubmissionDeadline
    ? hasDocDeadlinePassed
    : false

  if (isLoading) {
    return <Loading fullPage={false} />
  }

  return (
    <PageWrapper>
      {isAddMembersDialogOpen && (
        <DialogAddCommitteeMembers
          tenderData={tender}
          isOpen
          onClose={() => setIsAddMembersDialogOpen(false)}
          tenderCommission={tenderCommission}
        />
      )}

      {currentUser && (
        <TenderStickyHeader
          disabled={editingDisabled}
          tender={tender}
          onSaveTender={handleSubmit(onSubmit)}
          currentUser={currentUser}
          setValue={setValue}
        />
      )}
      <TabContext value={tab}>
        <TabList
          onChange={(_event, newValue) => {
            setTab(newValue)
            setPage(0)
          }}
          sx={{
            '&& .MuiTabs-flexContainer': {
              justifyContent: 'center',
            },
          }}
        >
          {tenderTabs
            .filter((tab) => !tab.disabled)
            .map(({ value, label }) => {
              if (value === TenderTabValues.QUESTION_ANSWERS) {
                return (
                  <Tab
                    sx={{ background: '#FFF' }}
                    key={value}
                    label={
                      <StyledBadge
                        color="error"
                        variant={pollData?.hasAnyUnreadMessage ? 'dot' : 'standard'}
                      >
                        {label}
                      </StyledBadge>
                    }
                    value={value}
                  />
                )
              }
              return (
                <Tab
                  sx={{ background: '#FFF' }}
                  key={value}
                  label={label}
                  value={value}
                />
              )
            })}
        </TabList>
        <TabPanel value={TenderTabValues.GENERAL}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack sx={{ mt: 2 }}>
              <CollapsibleSection
                title={t('tender-package')}
                additionalTitle={
                  <Box
                    display="flex"
                    flexGrow={1}
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    <Typography>
                      <span style={{ fontWeight: 700 }}>
                        {t('estimated-price-amount')}
                      </span>{' '}
                      {tender.lot.estimatedPriceAmount || ''}
                    </Typography>
                  </Box>
                }
              >
                <Grid container maxWidth="60%" margin="auto">
                  <Grid item xs={12}>
                    <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <TextField
                            {...field}
                            sx={{ mb: 1.5 }}
                            fullWidth
                            disabled
                            label={t('tender-name')}
                            variant="outlined"
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="organizationName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          sx={{ mb: 1.5 }}
                          fullWidth
                          disabled
                          label={t('organization-name')}
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      sx={{ mb: 1.5 }}
                      value={formatTimeStampToDate(tender.createTime)}
                      fullWidth
                      disabled
                      label={t('tender-creation-date')}
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="method"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={t(`${field.value}`)}
                          sx={{ mb: 1.5 }}
                          fullWidth
                          disabled
                          label={t('procurementMethod')}
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      sx={{ mb: 1.5 }}
                      value={tender.createdByUsername}
                      fullWidth
                      disabled
                      label={t('contestAuthor')}
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </CollapsibleSection>

              <CollapsibleSection title={t('buy-process-configuration')}>
                <>
                  <Grid container maxWidth="60%" margin="auto">
                    <Grid item xs={12}>
                      <Controller
                        control={control}
                        name="method"
                        render={({ field }) => (
                          <TextField
                            {...field}
                            value={t(`${field.value}`)}
                            sx={{ mb: 1.5 }}
                            fullWidth
                            disabled
                            label={t('procurementMethod')}
                            variant="outlined"
                          />
                        )}
                      />
                    </Grid>
                  </Grid>

                  <StepsSection
                    title={t('tender-process-steps')}
                    steps={[
                      t('uploading-docs'),
                      t('creating-tender-committee'),
                      t('forming-lot'),
                      t('forming-dates'),
                      t('forming-individual-invitations'),
                      t('forming-announcement'),
                    ]}
                  />
                </>
              </CollapsibleSection>

              <CollapsibleSection title={t('required-docs-for-tender')}>
                <Box>
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>{t('file-name')}</StyledTableCell>
                          <StyledTableCell>{t('file')}</StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>{t('protocol-no1')}</TableCell>
                          <TableCell>
                            <FileUpload
                              text={t('creationOrderFile')}
                              handleFile={(file) => {
                                setValue('protocolOneFileId', file.id)
                                createTender({
                                  ...tender,
                                  protocolOneFileId: file.id,
                                })
                              }}
                              onDelete={() => {
                                setValue('protocolOneFileId', '')
                                createTender({
                                  ...tender,
                                  protocolOneFileId: '',
                                })
                              }}
                              fileId={tender.protocolOneFileId ?? undefined}
                              accept="application/pdf"
                              isDisabled={editingDisabled}
                            />
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </CollapsibleSection>

              <CollapsibleSection title={t('tender-committee')}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="flex-end" spacing={1}>
                    {!!tenderCommission?.id && (
                      <FileUpload
                        text={t('upload-order')}
                        handleFile={(file) => {
                          handleCommissionFileUpload(file)
                        }}
                        fileId={tenderCommission?.orderFileId ?? undefined}
                        onDelete={handleCommissionFileDelete}
                        isDisabled={editingDisabled}
                      />
                    )}
                    <Button
                      color="primary"
                      variant="contained"
                      style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                      startIcon={<Add />}
                      disabled={editingDisabled}
                      onClick={() => {
                        setIsAddMembersDialogOpen(true)
                      }}
                    >
                      {t('add-members')}
                    </Button>
                  </Stack>
                  {tenderCommission &&
                    tenderCommission.tenderCommissionMembers &&
                    tenderCommission.tenderCommissionMembers.length > 0 && (
                      <TenderCommitteeMembersTable
                        isDisabled={editingDisabled}
                        tenderCommission={tenderCommission}
                      />
                    )}
                </Stack>
              </CollapsibleSection>

              <CollapsibleSection title={t('lot')}>
                <LotCard lot={tender.lot} />
              </CollapsibleSection>

              <CollapsibleSection title={t('tender-time-ranges')}>
                <Grid container spacing={4}>
                  <Grid xs={6} item>
                    <FormControl fullWidth margin="normal" required>
                      <Controller
                        control={control}
                        name="startTime"
                        render={({ field }) => (
                          <DateTimePicker
                            {...field}
                            label={t('tender-start-date-time')}
                            ampm={false}
                            slotProps={{ textField: { error: false } }}
                            disablePast={true}
                            onChange={field.onChange}
                            disabled={editingDisabled}
                            timeSteps={{
                              hours: 1,
                              minutes: 1,
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormControl fullWidth margin="normal">
                      <Controller
                        control={control}
                        name="envelopeOpeningTime"
                        render={({ field }) => (
                          <DateTimePicker
                            {...field}
                            label={t('envelopeOpeningTime')}
                            slotProps={{ textField: { error: false } }}
                            ampm={false}
                            minDate={envelopeOpeningMinDate}
                            disabled={
                              !tenderDocSubmissionDeadline || editingDisabled
                            }
                            onChange={field.onChange}
                            timeSteps={{
                              hours: 1,
                              minutes: 1,
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={6} item>
                    <FormControl fullWidth margin="normal">
                      <Controller
                        control={control}
                        name="docSubmissionDeadline"
                        render={({ field }) => (
                          <DateTimePicker
                            {...field}
                            slotProps={{ textField: { error: false } }}
                            label={t('documentSubmissionDeadline')}
                            ampm={false}
                            disabled={!tenderStartTime || editingDisabled}
                            minDate={tenderStartTime}
                            onChange={field.onChange}
                            timeSteps={{
                              hours: 1,
                              minutes: 1,
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <Grid
                      item
                      xs={12}
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignContent: 'center',
                        marginTop: '30px',
                      }}
                    >
                      <Button
                        type="submit"
                        size="small"
                        variant="contained"
                        style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                        color="primary"
                        disabled={
                          !tenderStartTime ||
                          !tenderDocSubmissionDeadline ||
                          !envelopeOpeningTime ||
                          editingDisabled
                        }
                      >
                        {t('save')}
                      </Button>
                    </Grid>
                  </Grid>
                </Grid>
              </CollapsibleSection>

              <CollapsibleSection title={t('tenderInvitationSectionTitle')}>
                <TenderInvitationSection
                  isDisabled={editingDisabled}
                  tenderId={tender?.id ?? ''}
                />
              </CollapsibleSection>

              <CollapsibleSection title={t('declaration')}>
                <Box
                  sx={(t) => ({
                    borderLeft: `3px solid ${t.palette.primary.main}`,
                    py: 1,
                    px: 2,
                    borderRadius: '2px',
                  })}
                >
                  <Grid container>
                    <Grid item xs={4}>
                      <Box>
                        <Typography>{t('organization-name')}:</Typography>
                        <Typography variant="body2">
                          {tender?.organizationName}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box>
                        <Typography>{t('tender-name')}:</Typography>
                        <Typography variant="body2">{tender?.name}</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={4}>
                      <Box>
                        <Typography>{t('startDate')}:</Typography>
                        <Typography variant="body2">
                          {tender?.startTime
                            ? formatTimeStampToDate(tender.startTime)
                            : null}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>

                <TenderContactPeople
                  isDisabled={editingDisabled}
                  tenderId={tender?.id ?? 0}
                />
              </CollapsibleSection>
            </Stack>
          </form>
        </TabPanel>
        <TabPanel value={TenderTabValues.QUESTION_ANSWERS}>
          <QuestionAndAnswers tender={tender} pollData={pollData} />
        </TabPanel>
      </TabContext>
    </PageWrapper>
  )
}

export const EditTenderPage = () => {
  const params = useParams<{ id: string }>()
  const {
    data: tender,
    isLoading,
    isError,
  } = useTenderQuery(
    { id: params.id ?? '' },
    { refetchOnMountOrArgChange: true, refetchOnFocus: true }
  )

  if (isLoading) return <Loading />
  if (isError) return <Error />

  if (tender === undefined) {
    return <></>
  }

  return <EditTender tender={tender} />
}
